/**
 * Cell selection and keyboard navigation (spec M1-B4 §6 "Selection model", §8.3).
 *
 * Pure functions over a flat, already-visible model of the grid, so every acceptance criterion
 * in group C is a unit test and the React layer only has to feed it the current geometry.
 *
 * Item (row) selection and cell selection are **independent** (§6): ticking checkboxes never
 * moves the cell focus, and vice versa.
 */
import type { ColumnId, ItemId } from '@elizmonday/shared';

export interface CellAddress {
  readonly itemId: ItemId;
  readonly columnId: ColumnId;
}

export interface GridSelection {
  /** Where a range starts. */
  readonly anchor: CellAddress | null;
  /** The "active" cell, always scrolled into view. */
  readonly focus: CellAddress | null;
  /** Rectangular, in visible order. */
  readonly range: { readonly itemIds: readonly ItemId[]; readonly columnIds: readonly ColumnId[] } | null;
  readonly editing: boolean;
}

export const EMPTY_SELECTION: GridSelection = {
  anchor: null,
  focus: null,
  range: null,
  editing: false,
};

/**
 * What the keyboard needs to know about the grid: the visible rows in display order (rows
 * only — never a header, a footer or the add-item row, §8.3) with the group each belongs to,
 * and the visible columns in display order (hidden columns are already gone).
 */
export interface GridGeometry {
  readonly rows: readonly { readonly itemId: ItemId; readonly groupId: string }[];
  readonly columnIds: readonly ColumnId[];
}

export type MoveDirection = 'up' | 'down' | 'left' | 'right';

export function rowIndex(geometry: GridGeometry, itemId: ItemId): number {
  return geometry.rows.findIndex((row) => row.itemId === itemId);
}

export function columnIndex(geometry: GridGeometry, columnId: ColumnId): number {
  return geometry.columnIds.indexOf(columnId);
}

export function cellAt(geometry: GridGeometry, row: number, column: number): CellAddress | null {
  const rowEntry = geometry.rows[row];
  const columnId = geometry.columnIds[column];
  if (!rowEntry || columnId === undefined) return null;
  return { itemId: rowEntry.itemId, columnId };
}

export function sameCell(a: CellAddress | null, b: CellAddress | null): boolean {
  if (!a || !b) return a === b;
  return a.itemId === b.itemId && a.columnId === b.columnId;
}

/**
 * §8.3: arrows move one cell, wrap at neither edge, skip hidden columns (they are not in
 * `columnIds`) and cross group boundaries into the next/previous group's rows.
 */
export function move(
  geometry: GridGeometry,
  from: CellAddress,
  direction: MoveDirection,
  jump = false,
): CellAddress | null {
  const row = rowIndex(geometry, from.itemId);
  const column = columnIndex(geometry, from.columnId);
  if (row < 0 || column < 0) return null;

  switch (direction) {
    case 'left':
      return cellAt(geometry, row, jump ? 0 : Math.max(0, column - 1));
    case 'right':
      return cellAt(
        geometry,
        row,
        jump ? geometry.columnIds.length - 1 : Math.min(geometry.columnIds.length - 1, column + 1),
      );
    case 'up':
      return cellAt(geometry, jump ? firstRowOfGroup(geometry, row) : Math.max(0, row - 1), column);
    case 'down':
      return cellAt(
        geometry,
        jump ? lastRowOfGroup(geometry, row) : Math.min(geometry.rows.length - 1, row + 1),
        column,
      );
  }
}

/** `Ctrl/Cmd+↑` — the first row of the focused cell's group (§8.3). */
export function firstRowOfGroup(geometry: GridGeometry, row: number): number {
  const groupId = geometry.rows[row]?.groupId;
  let index = row;
  while (index > 0 && geometry.rows[index - 1]?.groupId === groupId) index -= 1;
  return index;
}

export function lastRowOfGroup(geometry: GridGeometry, row: number): number {
  const groupId = geometry.rows[row]?.groupId;
  let index = row;
  while (index < geometry.rows.length - 1 && geometry.rows[index + 1]?.groupId === groupId) index += 1;
  return index;
}

/** `Tab` / `Shift+Tab`: at the last column, wrap to the first column of the next row (§8.3). */
export function tab(geometry: GridGeometry, from: CellAddress, back: boolean): CellAddress | null {
  const row = rowIndex(geometry, from.itemId);
  const column = columnIndex(geometry, from.columnId);
  if (row < 0 || column < 0) return null;
  const width = geometry.columnIds.length;
  const flat = row * width + column + (back ? -1 : 1);
  if (flat < 0 || flat >= geometry.rows.length * width) return null;
  return cellAt(geometry, Math.floor(flat / width), flat % width);
}

/** The rectangle between two addresses, in visible order (§6). */
export function rectangle(
  geometry: GridGeometry,
  anchor: CellAddress,
  focus: CellAddress,
): GridSelection['range'] {
  const r1 = rowIndex(geometry, anchor.itemId);
  const r2 = rowIndex(geometry, focus.itemId);
  const c1 = columnIndex(geometry, anchor.columnId);
  const c2 = columnIndex(geometry, focus.columnId);
  if (r1 < 0 || r2 < 0 || c1 < 0 || c2 < 0) return null;
  const rows = geometry.rows.slice(Math.min(r1, r2), Math.max(r1, r2) + 1).map((row) => row.itemId);
  const columnIds = geometry.columnIds.slice(Math.min(c1, c2), Math.max(c1, c2) + 1);
  return { itemIds: rows, columnIds };
}

export function focusCell(geometry: GridGeometry, address: CellAddress): GridSelection {
  return {
    anchor: address,
    focus: address,
    range: rectangle(geometry, address, address),
    editing: false,
  };
}

/** `Shift` + arrow / click: extend the rectangle from the anchor, keeping it (§8.3). */
export function extendTo(
  geometry: GridGeometry,
  selection: GridSelection,
  address: CellAddress,
): GridSelection {
  const anchor = selection.anchor ?? address;
  return {
    anchor,
    focus: address,
    range: rectangle(geometry, anchor, address),
    editing: false,
  };
}

export function moveFocus(
  geometry: GridGeometry,
  selection: GridSelection,
  direction: MoveDirection,
  options: { extend?: boolean; jump?: boolean } = {},
): GridSelection {
  if (!selection.focus) {
    const first = cellAt(geometry, 0, 0);
    return first ? focusCell(geometry, first) : selection;
  }
  const next = move(geometry, selection.focus, direction, options.jump ?? false);
  if (!next) return selection;
  return options.extend ? extendTo(geometry, selection, next) : focusCell(geometry, next);
}

/** `Ctrl/Cmd+A` — every cell of the focused cell's group (ASSUMED, Q169). */
export function selectGroup(geometry: GridGeometry, selection: GridSelection): GridSelection {
  if (!selection.focus) return selection;
  const row = rowIndex(geometry, selection.focus.itemId);
  if (row < 0) return selection;
  const first = cellAt(geometry, firstRowOfGroup(geometry, row), 0);
  const last = cellAt(geometry, lastRowOfGroup(geometry, row), geometry.columnIds.length - 1);
  if (!first || !last) return selection;
  return {
    anchor: first,
    focus: selection.focus,
    range: rectangle(geometry, first, last),
    editing: false,
  };
}

/** `Escape` twice: the first cancels the editor, the second clears the range (§8.3). */
export function escape(selection: GridSelection): GridSelection {
  if (selection.editing) return { ...selection, editing: false };
  if (!selection.focus) return EMPTY_SELECTION;
  return { anchor: selection.focus, focus: selection.focus, range: null, editing: false };
}

export function isInRange(selection: GridSelection, address: CellAddress): boolean {
  const range = selection.range;
  if (!range) return false;
  return range.itemIds.includes(address.itemId) && range.columnIds.includes(address.columnId);
}

/** Every address of the current range, row-major — what `Delete` and `Copy` iterate. */
export function rangeAddresses(selection: GridSelection): CellAddress[] {
  const range = selection.range;
  if (!range) return selection.focus ? [selection.focus] : [];
  const out: CellAddress[] = [];
  for (const itemId of range.itemIds) {
    for (const columnId of range.columnIds) out.push({ itemId, columnId });
  }
  return out;
}

/* ── row (item) selection — independent of the cell range `[B3§8.4]` ──────── */

export interface ItemSelection {
  readonly ids: readonly ItemId[];
  /** Where `Shift`+click measures from. */
  readonly anchor: ItemId | null;
}

export const EMPTY_ITEM_SELECTION: ItemSelection = { ids: [], anchor: null };

export function toggleItem(selection: ItemSelection, itemId: ItemId): ItemSelection {
  const has = selection.ids.includes(itemId);
  return {
    ids: has ? selection.ids.filter((id) => id !== itemId) : [...selection.ids, itemId],
    anchor: itemId,
  };
}

/** `Ctrl`/`Cmd`+click toggles without moving the anchor `[B3§8.4]`. */
export function toggleItemKeepAnchor(selection: ItemSelection, itemId: ItemId): ItemSelection {
  const has = selection.ids.includes(itemId);
  return {
    ids: has ? selection.ids.filter((id) => id !== itemId) : [...selection.ids, itemId],
    anchor: selection.anchor,
  };
}

/** `Shift`+click selects the inclusive range in **visible order**, crossing groups (Q160). */
export function extendItemRange(
  selection: ItemSelection,
  order: readonly ItemId[],
  itemId: ItemId,
): ItemSelection {
  if (!selection.anchor) return toggleItem(selection, itemId);
  const from = order.indexOf(selection.anchor);
  const to = order.indexOf(itemId);
  if (from < 0 || to < 0) return toggleItem(selection, itemId);
  const slice = order.slice(Math.min(from, to), Math.max(from, to) + 1);
  const merged = new Set(selection.ids);
  for (const id of slice) merged.add(id);
  return { ids: order.filter((id) => merged.has(id)), anchor: selection.anchor };
}

export function selectItems(order: readonly ItemId[], ids: readonly ItemId[]): ItemSelection {
  const wanted = new Set(ids);
  return { ids: order.filter((id) => wanted.has(id)), anchor: ids[ids.length - 1] ?? null };
}
